import { AccordionGroup, AccordionStep } from "@/components/ui/Accordion";
import { FormStepAmount } from "./FormStepAmount";
import { FormStepInformation } from "./FormStepInformation";
import { useRef, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { FormStepPayment } from "./FormStepPayment";
import { FormStepYourDetails } from "./FormStepYourDetails";
import { FormStepYourAddress } from "./FormStepYourAddress";
import { AmountLayout } from "@/layouts/AmountLayout";
import { DetailsLayout } from "@/layouts/DetailsLayout";
import { FormStepCardDetails } from "./FormStepCardDetails";
import { FormStepBankDetails } from "./FormStepBankDetails";
import { FormStepCardSelector } from "./FormStepCardSelector";
import { FormStepConfirmDetails } from "./FormStepConfirmDetails";
import { useDonationFormStore } from "@/stores";
import { FormStepSuccess } from "./FormStepSuccess";
import { Loading } from "@/components/ui/Loading";
import { useService } from "@/hooks/useService";
import type { FormProps } from "./FormProps";
import type { SubmissionSuccessResponse } from "@/types/Payment";
import { ImperativeHandle } from "@/types/Form";
import { trackError } from "@/utils/error-monitoring";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import classes from "./Form.module.scss";
import { useErrorProcessing } from "@/hooks/useErrorProcessing";
import type { ServerErrorResponse } from "@/types/ServerError";
import { useNavigate } from "@/hooks/useNavigate";
import { Screen, DetailsStep } from "@/types/Navigation";
import { useAppConfig } from "@/hooks/useAppConfig";
import { EventName } from "@/types/Events";
import { dispatchEvent } from "@/utils/event-dispacher";
import { useBraintree } from "@/hooks/useBraintree";
import { PAYMENT_ERROR_CODE } from "@/config";
import { ErrorFallback } from "@/components/ErrorFallback";

type StepRefs = {
  [key: string]: ImperativeHandle | null;
};

export function Form(props: FormProps) {
  const { id } = props;
  const [processing, setProcessing] = useState<boolean>(false);
  const [hasErrorSubmitted, setHasErrorSubmitted] = useState<boolean>(false);
  const [showPaymentError, setShowPaymentError] = useState<boolean>(false);

  const { processErrors } = useErrorProcessing();
  const {
    next,
    navigateTo,
    screen,
    currentDetailsStep,
    visibleDetailsSteps,
    removeCurrentDetailsStep,
  } = useNavigate();
  const { regenerateClient } = useBraintree();
  const { accordionTitles, confirmationText } = useAppConfig();

  const { sendSubmission } = useService();
  const [submissionResponse, setSubmissionResponse] =
    useState<SubmissionSuccessResponse | null>(null);

  const { getSubmissionFields, getPayment } = useDonationFormStore();

  const stepRefs = useRef<StepRefs>({
    [DetailsStep.SelectCard]: null,
    [DetailsStep.DonationInformation]: null,
    [DetailsStep.YourDetails]: null,
    [DetailsStep.YourAddress]: null,
  });

  const handleSubmit = async () => {
    for (const name in stepRefs.current) {
      const ref = stepRefs.current[name];
      if (!ref) continue;

      const { isValid } = await ref.validate();

      if (!isValid) {
        navigateTo(name as DetailsStep);
        return;
      }
    }
    removeCurrentDetailsStep();
    setProcessing(true);

    try {
      const payment = getPayment();
      const submissionFields = getSubmissionFields();

      const submission = {
        form_id: id,
        fields: submissionFields,
        ...(payment && {
          payment,
        }),
      };

      dispatchEvent(EventName.FORM_SUBMIT, submission);
      const response = await sendSubmission(submission);

      if (!response.success) {
        throw new Error("Submission failed");
      }

      dispatchEvent(EventName.PURCHASE, response.datalayer);
      setSubmissionResponse(response);
      next();
    } catch (serverError) {
      regenerateClient();

      const error = serverError as unknown as ServerErrorResponse;
      dispatchEvent(EventName.PURCHASE_ERROR, error);

      if (error?.error_type === PAYMENT_ERROR_CODE) {
        setShowPaymentError(true);
        return;
      }

      if (error?.errors) {
        const { captured, processorName } = processErrors(error.errors);
        if (captured && processorName?.startsWith("donation_source")) {
          navigateTo(DetailsStep.DonationInformation);
          return;
        }

        if (captured && processorName?.startsWith("personalDetails")) {
          navigateTo(DetailsStep.YourDetails);
          return;
        }

        if (captured && processorName?.startsWith("your_address")) {
          navigateTo(DetailsStep.YourAddress);
          return;
        }

        if (captured && processorName === "card_details") {
          navigateTo(DetailsStep.CardDetails);
          return;
        }

        if (captured && processorName === "payment_methods") {
          navigateTo(DetailsStep.PaymentType);
          return;
        }
      }

      trackError(serverError as Error);
      setHasErrorSubmitted(true);
    } finally {
      setProcessing(false);
    }
  };

  const t = useTranslation("_common");

  if (showPaymentError) {
    return <ErrorFallback />;
  }

  if (screen === Screen.Amount) {
    return (
      <AmountLayout>
        <FormStepAmount onStepSubmit={() => next()} />
      </AmountLayout>
    );
  }

  if (screen === Screen.Success) {
    return <FormStepSuccess submissionResponse={submissionResponse!} />;
  }

  return (
    <DetailsLayout>
      <AccordionGroup
        disabled={processing}
        value={currentDetailsStep}
        onChange={(value) => {
          navigateTo(value as DetailsStep);
        }}
      >
        <AccordionStep
          title={accordionTitles.createCard}
          value={DetailsStep.SelectCard}
          visible={visibleDetailsSteps.includes(DetailsStep.SelectCard)}
          forceScroll={visibleDetailsSteps.length > 1}
        >
          <FormStepCardSelector
            ref={(ref) => {
              stepRefs.current[DetailsStep.SelectCard] = ref;
            }}
            onStepSubmit={() => next()}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.donationInfo}
          value={DetailsStep.DonationInformation}
          visible={visibleDetailsSteps.includes(
            DetailsStep.DonationInformation,
          )}
          forceScroll={visibleDetailsSteps.length > 1}
        >
          <FormStepInformation
            ref={(ref) => {
              stepRefs.current[DetailsStep.DonationInformation] = ref;
            }}
            onStepSubmit={() => next()}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.paymentType}
          value={DetailsStep.PaymentType}
          visible={visibleDetailsSteps.includes(DetailsStep.PaymentType)}
          forceScroll
        >
          <FormStepPayment onStepSubmit={() => next()} />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.yourDetails}
          value={DetailsStep.YourDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.YourDetails)}
          forceScroll
        >
          <FormStepYourDetails
            ref={(ref) => {
              stepRefs.current[DetailsStep.YourDetails] = ref;
            }}
            onStepSubmit={() => next()}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.yourAddress}
          value={DetailsStep.YourAddress}
          visible={visibleDetailsSteps.includes(DetailsStep.YourAddress)}
          forceScroll
        >
          <FormStepYourAddress
            ref={(ref) => {
              stepRefs.current[DetailsStep.YourAddress] = ref;
            }}
            onStepSubmit={() => next()}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.confirmDetails}
          value={DetailsStep.ConfirmDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.ConfirmDetails)}
          forceScroll
        >
          <FormStepConfirmDetails
            message={confirmationText}
            onStepSubmit={handleSubmit}
          />
        </AccordionStep>

        <AccordionStep
          title={t("bank-details")}
          value={DetailsStep.BankDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.BankDetails)}
          forceScroll
        >
          <FormStepBankDetails onStepSubmit={handleSubmit} />
        </AccordionStep>

        <AccordionStep
          title={t("card-details")}
          value={DetailsStep.CardDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.CardDetails)}
          forceScroll
        >
          <FormStepCardDetails onStepSubmit={handleSubmit} />
        </AccordionStep>
      </AccordionGroup>

      {hasErrorSubmitted && (
        <div className={classes.pageError}>
          <Alert variant="error">Error submitting donation</Alert>

          <div className={classes.pageErrorActions}>
            <Button
              onClick={() => {
                const lastVisibleStep =
                  visibleDetailsSteps[visibleDetailsSteps.length - 1];
                if (!lastVisibleStep) return;

                navigateTo(lastVisibleStep);
                setHasErrorSubmitted(false);
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      )}

      {processing && (
        <div>
          <Loading message="Donation processing" />
        </div>
      )}
    </DetailsLayout>
  );
}
